import cx from 'classnames';
import debounce from 'lodash/debounce';
import {
  CSSProperties,
  FC,
  MouseEvent,
  TouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {shallowEqual} from 'react-redux';
import {useIsLargishAndUp, useIsMediumAndUp} from '~/common/hooks/useMatchQuery';
import {useCuratedSets} from '~/QuickEcards/hooks/useCuratedSets';
import {useAppSelector} from '~/QuickEcards/store';
import {selectFabricArt, selectHasGiftCard} from '~/QuickEcards/store/reducers/ecard';
import {resizeText} from '~/QuickEcards/utils/ecard';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {Video} from '~library/atoms/Video';
import {TriangleIcon} from '~library/svg/TriangleIcon';

import './ECard.sass';

interface ECardProps {
  message: string | null;
  animation?: string;
  setAnimation?: (anim: string) => void;
  animationFinished: boolean;
  preview?: boolean;
  replayAnimation?: () => void;
  openFullPreview?: () => void;
  handleCardToggle: () => void;
  disableFlip?: boolean;
}

export const ECard: FC<ECardProps> = ({
  message,
  animation = '',
  setAnimation,
  animationFinished = false,
  preview = false,
  replayAnimation,
  openFullPreview,
  // biome-ignore lint/suspicious/noEmptyBlockStatements: existing error
  handleCardToggle = () => {},
  disableFlip = false,
}: ECardProps) => {
  const replayButtonRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dragStartPos = useRef<number>(0);
  const dragEndPos = useRef<number>(0);

  const fabricArt = useAppSelector(selectFabricArt, shallowEqual);
  const hasGiftCard = useAppSelector(selectHasGiftCard);
  const {curatedSet} = useCuratedSets();
  const isMediumAndUp = useIsMediumAndUp();
  const isDesktop = useIsLargishAndUp();
  const templateAsset = fabricArt?.backgroundImage?.src;
  const isMp4 = templateAsset?.endsWith('.mp4');

  const backStyle: CSSProperties | undefined = useMemo(() => {
    if (curatedSet && 'matching_set' in curatedSet) {
      return {
        backgroundColor: curatedSet?.matching_set.card_back.background_color,
        color: curatedSet?.matching_set.card_back.color,
      };
    }
    return undefined;
  }, [curatedSet]);

  const handleResize = useCallback(() => {
    const messageElem = document.getElementById('ecard__message');
    if (!messageElem) return;
    resizeText(messageElem, isMediumAndUp ? 2.5 : 1.5, 'rem');
  }, [isMediumAndUp]);

  const debouncedHandleResize = useMemo(() => debounce(handleResize, 50), [handleResize]);

  useEffect(() => {
    // Resize message when message changes
    if (!message) return;
    handleResize();
  }, [handleResize, message]);

  useEffect(() => {
    // Resize message when window gets resized
    debouncedHandleResize();
    window.addEventListener('resize', debouncedHandleResize);
    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, [debouncedHandleResize]);

  useEffect(() => {
    const DESKTOP_PREVIEW_TOP_BOTTOM_SPACER = 224;
    const DEFAULT_TOP_BOTTOM_SPACER = (isDesktop ? 32 : 0) + 112;
    const TOP_BOTTOM_SPACER =
      isDesktop && preview ? DESKTOP_PREVIEW_TOP_BOTTOM_SPACER : DEFAULT_TOP_BOTTOM_SPACER;
    const HEADER_HEIGHT = preview ? 54 : 0;

    const card = document.getElementById('ecard__front');
    const reactionDrawer = document.getElementById('ecard__reaction-drawer');
    if (!card || !reactionDrawer) return;

    const previewBanner = document.getElementById('quick-ecards__preview-banner');
    const PREVIEW_BANNER_HEIGHT = previewBanner?.clientHeight ?? 0;

    card.style.maxHeight = `calc(100dvh - ${reactionDrawer.clientHeight + PREVIEW_BANNER_HEIGHT + HEADER_HEIGHT + TOP_BOTTOM_SPACER}px)`;
  }, [isDesktop, preview]);

  const handleSwipeStart = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!message || disableFlip) return;
      if (animation === 'unflip') {
        e.preventDefault();
        e.stopPropagation();
      }
      dragStartPos.current = 'clientX' in e ? e.clientX : e.changedTouches[0].screenX;
    },
    [animation, disableFlip, message]
  );

  const handleSwipeEnd = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!message || disableFlip) return;
      const selection = window.getSelection();
      if (selection && selection.type === 'Range') return;

      dragEndPos.current = 'clientX' in e ? e.clientX : e.changedTouches[0].screenX;
      if (animation === 'flip' && dragEndPos.current - dragStartPos.current >= 50) {
        // User has swiped to flip the card back to the front
        setAnimation?.('unflip');
      } else if (animation === 'unflip' && dragEndPos.current - dragStartPos.current <= -50) {
        // User has swiped to flip the card to the back
        setAnimation?.('flip');
      }
    },
    [animation, disableFlip, message, setAnimation]
  );

  return (
    <div
      ref={wrapperRef}
      className={cx('ecard__wrapper', {
        'ecard__wrapper--preview': preview,
        'ecard__wrapper--with-egift': hasGiftCard,
      })}
    >
      {/** biome-ignore lint/a11y/noStaticElementInteractions: existing error */}
      <div
        className={cx('ecard__content', {
          [`ecard__content--${animation}-animation`]: animation,
        })}
        onTouchStart={handleSwipeStart}
        onTouchEnd={handleSwipeEnd}
        onMouseDown={handleSwipeStart}
        onMouseUp={handleSwipeEnd}
        role="presentation"
      >
        {isMp4 && templateAsset ? (
          <Video src={templateAsset} className="ecard__front" id="ecard__front" />
        ) : (
          <img className="ecard__front" id="ecard__front" src={templateAsset} alt="ecard design" />
        )}
        <div className="ecard__back" style={backStyle}>
          {message && (
            <Typography classNames="ecard__message" variant="paragraph1" id="ecard__message">
              {message}
            </Typography>
          )}
        </div>
      </div>
      {message && !disableFlip && (
        <div
          className={cx('ecard__controls', {
            'ecard__controls--visible': animationFinished,
          })}
        >
          <Button
            variant="unstyled-button"
            className={cx('ecard__toggle', {
              // biome-ignore lint/complexity/useLiteralKeys: existing error
              [`ecard__toggle--flip`]: animation === 'flip',
            })}
            onClick={handleCardToggle}
            ariaLabel="toggle-design-or-message"
            data-qa-id="greeting-card-toggle"
          />
        </div>
      )}
      {replayAnimation && (
        <Button
          variant="unstyled-button"
          className={cx('ecard__replay-button', {
            'ecard__controls--visible': animationFinished,
          })}
          ref={replayButtonRef}
          onClick={replayAnimation}
          data-qa-id="replay-animation"
        >
          <TriangleIcon ratio={0.75} />
          Replay
        </Button>
      )}
      {openFullPreview && !replayAnimation && (
        <Button
          variant="unstyled-button"
          className="ecard__preview-button"
          onClick={openFullPreview}
          data-qa-id="open-preview"
        >
          <TriangleIcon ratio={0.75} />
          Preview
        </Button>
      )}
    </div>
  );
};
